import { Component, OnInit, Input, OnDestroy, OnChanges, SimpleChanges, DoCheck, ChangeDetectionStrategy } from '@angular/core';
import { Payer } from '../../model/payer';
import { FormGroup, FormBuilder, FormArray } from '@angular/forms';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { ActionWindowService } from '../../../core/service/action-window.service';
import { Contact } from '../../model/contact';
import { ContactBalanceTransactionService } from '../../../invoice/service/data/contact-balance-transaction.service';
import { PaymentMethodService } from '../../../invoice/service/data/payment-method.service';
import { SelectionItem } from '../../../core/component/select/select.component';
import { LocalDate } from '../../../core/model/local-date';
import { switchMap, tap } from 'rxjs/operators';
import { ContactService } from '../../service/data/contact.service';
import { MessageService } from '../../../core/service/message.service';
import { Subscription } from 'rxjs';
import { Tenant } from '../../model/tenant';
import { AutoCompleteSelectionItem, IAutoCompleteService } from '../../../core/component/autocomplete-remote/autocomplete-remote.component';
import { TenantService } from '../../service/data/tenant.service';
import { LeaseAgreementService } from '../../service/data/lease-agreement.service';
import { FormControlEx } from '../../../core/component/forms/form-control';
import { Money } from '../../../core/model/money';
import { ContactCompanyBalance } from '../../../invoice/model/contact-company-balance';
import { ContactBalanceMove, ContactCompanyBalanceMove } from '../../../invoice/model/contact-balance-move';

@Component({
    selector: 'app-move-balance-form',
    templateUrl: './move-balance-form.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class MoveBalanceFormComponent implements OnInit, OnDestroy {
  constructor(
    private _dialogService: ActionWindowService,
    private _balanceService: ContactBalanceTransactionService,
    private _contactService: ContactService,
    private _messageService: MessageService,
    private _fb: FormBuilder,
    private _fcf: FormControlExFactory,
    private _paymentMethodService: PaymentMethodService,
    filterService: TenantService,
    private leaseAgreementService: LeaseAgreementService,
    private contactService: ContactService
  ) {
    this.filterService = filterService;
  }

  private _dataContext: Contact;
  @Input() set dataContext(dataContext: Contact) {
    this._dataContext = dataContext;
  }

  get dataContext(): Contact {
    return this._dataContext;
  }

  form: FormGroup;
  forms: FormArray;
  private _sub: Subscription = new Subscription();
  private tenant: Tenant;
  filterService: TenantService;
  private companyId: string;
  cancel() {
    this._dialogService.close();
  }

  ngOnDestroy(): void {
    this._sub.unsubscribe();
    if (this._repaySub) this._repaySub.unsubscribe();
  }

  balances: ContactCompanyBalance[];

  accountSelections: SelectionItem[][];

  ngOnInit() {
    this._sub.add(
      this._balanceService.getCompanyBalances(this.dataContext.id).subscribe(balances => {
        this.accountSelections = [];
        for (let balance of balances) {
          let items = balance.companyBankAccounts.map(a => new SelectionItem(a.name, a.id));
          items.splice(0, 0, new SelectionItem('CONTACT_BALANCE_TRANSACTION.CASH_PAYMENT', null));
          this.accountSelections.push(items);
        }

        this.balances = balances;
        this.forms = this._fb.array(
          balances.map(balance =>
            this._fb.group({
              amount: this._fcf.createWithOptions('OUTBOUND_PAYMENT.AMOUNT', balance.balance, {
                required: true,
                maxValue: balance.balance.amount,
                minValue: 0.01,
              }),
              companyId: this._fcf.createHidden(balance.companyId),
              companyCaption: this._fcf.createReadonly('COMMON.COMPANY', balance.companyCaption),
              contactId: this._fcf.create('COMMON.TENANT', null),
            })
          )
        );

        this.form = this._fb.group({
          repayments: this.forms,
        });
      })
    );
  }

  private _working = false;
  private _repaySub: Subscription;

  onSubmit() {
    if (this.form.invalid || this._working) return;

    this._working = true;
    let errorMessage: string = '';
    let arBalances = new Array<ContactCompanyBalanceMove>();
    for (const repayment of this.form.value.repayments) {
      const companyId: string = repayment.companyId;
      const amount: Money = repayment.amount;
      const contactId: string = repayment.contactId;

      const balance: ContactCompanyBalance = this.balances.find(x => x.companyId === companyId);
      if (balance) {
        if (amount.amount > balance.balance.amount) {
          errorMessage = 'CONTACT_BALANCE_TRANSACTION.ERROR.BALANCE_TOO_HIGH';
        }
      } else {
        //error in form
        errorMessage = 'ERROR.UNKNOWN';
      }

      let newBalance = new ContactCompanyBalanceMove();
      newBalance.amount = amount;
      newBalance.companyId = companyId;
      newBalance.toContactId = contactId;
      arBalances.push(newBalance);
    }

    if (errorMessage.length > 0) {
      this._messageService.showError(errorMessage);
      this._working = false;
      return;
    }

    const balancesToMove = this.createContactBalances(this.dataContext.id, arBalances);
    this._sub.add(
      this._balanceService.moveBalance(balancesToMove).subscribe(res => {
        this._dialogService.complete(res);
      })
    );

    this._working = false;
  }

  okButtonDisabled = true;

  onTenantChanged(tenant: Tenant) {
    console.log(tenant.name);
  }

  onContactSelected(item: AutoCompleteSelectionItem, target: FormControlEx, companyId: string) {
    this.okButtonDisabled = true;
    this.tenant = null;
    const tenant = item?.data as Tenant;
    if (!tenant) {
      this.okButtonDisabled = true;
      return;
    }

    this._sub.add(
      this.contactService.get(tenant.id).subscribe(x => {
        if (x.leaseAgreements.findIndex(y => y.companyId === companyId) !== -1) {
          this.tenant = tenant;
          this.okButtonDisabled = false;
        } else {
          this._messageService.showWarning('CONTACT_BALANCE_TRANSACTION.ERROR.CONTACT_WRONG_COMPANY');
          target.setValue('', { emitEvent: false });
          this.okButtonDisabled = true;
        }
      })
    );
  }

  private createContactBalances(fromContactId: string, balances: ContactCompanyBalanceMove[]): ContactBalanceMove {
    let balance = new ContactBalanceMove();
    balance.fromContactId = fromContactId;
    balance.movements = balances;
    return balance;
  }
}
